import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Keyboard } from 'react-native';
import { IContextSheetContentProps, ISheetConfig } from '@/components/ui/ContextSheet';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Text } from '@/components/gluestack/text';
import { Input, InputField } from '@/components/gluestack/input';
import { Textarea, TextareaInput } from '@/components/gluestack/textarea';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { Pressable } from '@/components/gluestack/pressable';
import { Tick02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { useTranslation } from 'react-i18next';
import { useMeasurementFormatters, KG_TO_LBS, KG_TO_ST, LBS_TO_KG, ST_TO_KG, type WeightUnit } from '~/components/providers/MeasurementsProvider';
import { WeightsService } from '@/services/domain/WeightsService';
import { WeightRecord } from '@/models/Weight';
import { useToastMessages } from '@/hooks/useToastMessages';
import { ContextSheet } from '@/components/ui/ContextSheet';
import DatePickerSheet from '@/components/ui/DatePickerSheet';
import dayjs from '@/i18n/dayjs-config';

function convertKgToDisplay(kg: number, unit: WeightUnit): string {
  if (unit === 'kg') return String(Math.round(kg));
  if (unit === 'st') return (kg * KG_TO_ST).toFixed(1);
  return String(Math.round(kg * KG_TO_LBS));
}

interface WeightRecordSheetPayload {
  record?: WeightRecord | null;
  onSaved?: () => void;
  /** Pass from parent to avoid losing context when sheet is rendered in a portal (Drawer/Modal/Actionsheet). */
  weightUnit?: WeightUnit;
}

export default function WeightRecordSheet({
  onClose,
  payload,
}: IContextSheetContentProps & { payload?: WeightRecordSheetPayload }) {
  const record = payload?.record ?? null;
  const onSaved = payload?.onSaved;

  const { t } = useTranslation();
  const formatters = useMeasurementFormatters();
  const weightUnit = payload?.weightUnit ?? formatters.weightUnit;
  const { showSuccessToast, showErrorToast } = useToastMessages();

  const isEditing = record != null;

  // Convert display unit to kg
  const displayToKg = useCallback(
    (val: number): number => {
      if (weightUnit === 'kg') return val;
      if (weightUnit === 'st') return val * ST_TO_KG;
      return val * LBS_TO_KG;
    },
    [weightUnit]
  );

  const unitSuffix = useMemo(() => {
    if (weightUnit === 'kg') return 'kg';
    if (weightUnit === 'st') return 'st';
    return 'lbs';
  }, [weightUnit]);

  // State
  const [date, setDate] = useState<Date>(() => {
    if (!record) return new Date();
    const d = dayjs.utc(record.DateWeighted);
    return new Date(d.year(), d.month(), d.date());
  });
  const [weightText, setWeightText] = useState<string>(
    record ? convertKgToDisplay(record.WeightKg, weightUnit) : ''
  );
  const [notes, setNotes] = useState<string>(record?.Notes ?? '');

  // Re-convert weight text when weightUnit changes (handles async preference loading)
  const hasUserEdited = useRef(false);
  useEffect(() => {
    if (record && !hasUserEdited.current) {
      setWeightText(convertKgToDisplay(record.WeightKg, weightUnit));
    }
  }, [weightUnit, record]);

  const handleWeightTextChange = useCallback((text: string) => {
    hasUserEdited.current = true;
    setWeightText(text);
  }, []);
  const [isSaving, setIsSaving] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const weightValue = parseFloat(weightText);
  const isWeightValid = !isNaN(weightValue) && weightValue > 0;
  const maxWeight = weightUnit === 'kg' ? 453 : weightUnit === 'st' ? 71 : 999;
  const isWeightOverMax = isWeightValid && weightValue > maxWeight;
  const canSave = isWeightValid && !isWeightOverMax && !isSaving;

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    Keyboard.dismiss();
    setIsSaving(true);

    try {
      const weightKg = Math.round(displayToKg(weightValue) * 100) / 100;
      const dateStr = dayjs(date).format('YYYY-MM-DD') + 'T00:00:00';
      const payload = {
        DateWeighted: dateStr,
        WeightKg: weightKg,
        Notes: notes.trim() || undefined,
      };

      if (isEditing) {
        await WeightsService.updateWeight(record!.Id, payload);
      } else {
        await WeightsService.createWeight(payload);
      }

      showSuccessToast(
        isEditing
          ? t('weightHistory.updated', 'Weight updated')
          : t('weightHistory.added', 'Weight added')
      );
      onSaved?.();
    } catch (e) {
      showErrorToast(t('weightHistory.saveFailed', 'Failed to save'), e as any);
    } finally {
      setIsSaving(false);
    }
  }, [canSave, displayToKg, weightValue, date, notes, isEditing, record, showSuccessToast, showErrorToast, t, onSaved]);

  return (
    <VStack className="gap-4">
      {/* Date field */}
      <VStack className="gap-1">
        <Text size="sm" className="font-medium text-typography-500">
          {t('weightHistory.date', 'Date')}
        </Text>
        <Pressable onPress={() => setIsDatePickerOpen(true)}>
          <Input
            size="md"
            variant="outline"
            isReadOnly
            className="bg-typography-0"
            pointerEvents="box-none">
            <InputField
              value={dayjs(date).format('MMM D, YYYY')}
              editable={false}
              pointerEvents="none"
              tabIndex={-1}
            />
          </Input>
        </Pressable>
      </VStack>

      {/* Weight field */}
      <VStack className="gap-1">
        <Text size="sm" className="font-medium text-typography-500">
          {t('weightHistory.weight', 'Weight')} ({unitSuffix})
        </Text>
        <Input
          size="md"
          variant="outline"
          isInvalid={weightText.length > 0 && (!isWeightValid || isWeightOverMax)}
          className="bg-typography-0">
          <InputField
            value={weightText}
            onChangeText={handleWeightTextChange}
            placeholder="0"
            keyboardType="decimal-pad"
            returnKeyType="done"
            autoFocus={false}
          />
        </Input>
        {isWeightOverMax && (
          <Text size="xs" className="text-error-500">
            {t('editProfile.weightMaxError', 'Maximum weight is {{max}} {{unit}}', {
              max: maxWeight,
              unit: unitSuffix,
            })}
          </Text>
        )}
      </VStack>

      {/* Notes field */}
      <VStack className="gap-1">
        <Text size="sm" className="font-medium text-typography-500">
          {t('weightHistory.notes', 'Notes')}
        </Text>
        <Textarea size="md" className="min-h-20">
          <TextareaInput
            value={notes}
            onChangeText={setNotes}
            placeholder={t('weightHistory.notesPlaceholder', 'Optional notes...')}
            multiline
            textAlignVertical="top"
            className="m-0 p-2"
          />
        </Textarea>
      </VStack>

      {/* Action buttons */}
      <HStack
        className="items-center justify-end gap-3 pt-2">
        <ButtonWrapper
          icon={Tick02Icon}
          text={isSaving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
          onPress={handleSave}
          isDisabled={!canSave}
        />
      </HStack>

      {/* Date Picker Sheet */}
      <ContextSheet
        isOpen={isDatePickerOpen}
        onClose={() => setIsDatePickerOpen(false)}
        contentComponent={DatePickerSheet}
        title={t('weightHistory.selectDate', 'Select date')}
        payload={{
          selectedDate: date,
          onDateChange: (newDate: Date) => {
            setDate(newDate);
            setIsDatePickerOpen(false);
          },
          maxDate: new Date(),
        }}
      />
    </VStack>
  );
}

WeightRecordSheet.sheetConfig = {
  height: 'lg',
} satisfies ISheetConfig;
